import React, { useState } from "react";
import {
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
  Button,
  Datagrid,
  DateField,
  NumberField,
  TextField,
  TextInput,
  NumberInput,
  useGetList,
  ReferenceInput,
  SelectInput,
} from "react-admin";

export const SpeakerCreditsTab = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [credits, setCredits] = useState("");
  const [source, setSource] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const speakerId = record?.id;

  // Fetch credit periods
  const { data: periods } = useGetList(
    "ta-credit-periods",
    {
      sort: { field: "start_date", order: "DESC" },
      pagination: { page: 1, perPage: 100 },
    }
  );

  // Fetch credit transactions for this speaker
  const { data: transactions, isLoading, refetch } = useGetList(
    "ta-credit-transactions",
    {
      filter: { speaker_id: speakerId },
      sort: { field: "created_at", order: "ASC" },
      pagination: { page: 1, perPage: 1000 },
    },
    { enabled: !!speakerId }
  );

  // Group transactions by period
  const transactionsByPeriod = {};
  const unassignedTransactions = [];

  transactions?.forEach((transaction) => {
    if (transaction.ta_credit_period_id) {
      if (!transactionsByPeriod[transaction.ta_credit_period_id]) {
        transactionsByPeriod[transaction.ta_credit_period_id] = [];
      }
      transactionsByPeriod[transaction.ta_credit_period_id].push(transaction);
    } else {
      unassignedTransactions.push(transaction);
    }
  });

  // Calculate running totals for each period
  const periodsWithTotals = periods?.map((period) => {
    const periodTransactions = transactionsByPeriod[period.id] || [];
    let runningTotal = 0;
    const transactionsWithTotal = periodTransactions.map((transaction) => {
      runningTotal += transaction.credits;
      return {
        ...transaction,
        runningTotal,
      };
    });
    return {
      period,
      transactions: transactionsWithTotal,
      balance: runningTotal,
    };
  }) || [];

  // Calculate unassigned transactions total
  let unassignedRunningTotal = 0;
  const unassignedWithTotal = unassignedTransactions.map((transaction) => {
    unassignedRunningTotal += transaction.credits;
    return {
      ...transaction,
      runningTotal: unassignedRunningTotal,
    };
  });

  const totalBalance = transactions?.reduce((sum, t) => sum + t.credits, 0) || 0;

  const handleAddCredits = async () => {
    if (!credits || !source) {
      notify("Please enter both credits and source", { type: "warning" });
      return;
    }

    setIsSubmitting(true);
    try {
      await dataProvider.create("ta-credit-transactions", {
        data: {
          speaker_id: speakerId,
          credits: parseInt(credits),
          source,
          ta_credit_period_id: periodId || null,
        },
      });
      notify("Credits added successfully", { type: "success" });
      setCredits("");
      setSource("");
      setPeriodId("");
      refetch();
      refresh();
    } catch (error) {
      notify(`Error: ${error.message}`, { type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTransactionTable = (transactions, showPeriodColumn = false) => {
    if (transactions.length === 0) {
      return <div style={{ padding: "10px", color: "#999" }}>No transactions</div>;
    }

    return (
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          border: "1px solid #ddd",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#f5f5f5" }}>
            <th style={{ padding: "10px", textAlign: "left", border: "1px solid #ddd" }}>
              Date
            </th>
            <th style={{ padding: "10px", textAlign: "left", border: "1px solid #ddd" }}>
              Credits
            </th>
            <th style={{ padding: "10px", textAlign: "left", border: "1px solid #ddd" }}>
              Source
            </th>
            <th style={{ padding: "10px", textAlign: "right", border: "1px solid #ddd" }}>
              Running Total
            </th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((transaction) => (
            <tr key={transaction.id}>
              <td style={{ padding: "10px", border: "1px solid #ddd" }}>
                {new Date(transaction.created_at).toLocaleString()}
              </td>
              <td
                style={{
                  padding: "10px",
                  border: "1px solid #ddd",
                  color: transaction.credits >= 0 ? "green" : "red",
                  fontWeight: "bold",
                }}
              >
                {transaction.credits >= 0 ? "+" : ""}
                {transaction.credits}
              </td>
              <td style={{ padding: "10px", border: "1px solid #ddd" }}>
                {transaction.source}
              </td>
              <td
                style={{
                  padding: "10px",
                  border: "1px solid #ddd",
                  textAlign: "right",
                  fontWeight: "bold",
                }}
              >
                ${transaction.runningTotal}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  if (!speakerId) {
    return <div>No speaker selected</div>;
  }

  return (
    <div style={{ padding: "20px" }}>
      <h3>Total Balance (All Periods): ${totalBalance}</h3>

      <div
        style={{
          marginBottom: "30px",
          padding: "20px",
          border: "1px solid #ddd",
          borderRadius: "4px",
          backgroundColor: "#f9f9f9",
        }}
      >
        <h4>Add Credit Transaction</h4>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: "0 0 150px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Credits
            </label>
            <input
              type="number"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              placeholder="Amount (+ or -)"
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          </div>
          <div style={{ flex: "0 0 200px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Period (Optional)
            </label>
            <select
              value={periodId}
              onChange={(e) => setPeriodId(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            >
              <option value="">No Period</option>
              {periods?.map((period) => (
                <option key={period.id} value={period.id}>
                  {period.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              Source
            </label>
            <input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="e.g., Campaign fundraising, Credit card charge, Gift"
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          </div>
          <div style={{ paddingTop: "27px" }}>
            <Button
              label="Add"
              onClick={handleAddCredits}
              disabled={isSubmitting || !credits || !source}
            />
          </div>
        </div>
      </div>

      <h4>Transaction History by Period</h4>
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <>
          {periodsWithTotals.map(({ period, transactions, balance }) => (
            <div key={period.id} style={{ marginBottom: "30px" }}>
              <h5 style={{
                backgroundColor: "#e3f2fd",
                padding: "10px",
                borderRadius: "4px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span>{period.name}</span>
                <span style={{ fontWeight: "bold", color: balance >= 0 ? "green" : "red" }}>
                  Balance: ${balance}
                </span>
              </h5>
              {renderTransactionTable(transactions)}
            </div>
          ))}

          {unassignedWithTotal.length > 0 && (
            <div style={{ marginBottom: "30px" }}>
              <h5 style={{
                backgroundColor: "#f5f5f5",
                padding: "10px",
                borderRadius: "4px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span>Unassigned to Period</span>
                <span style={{ fontWeight: "bold" }}>
                  Balance: ${unassignedRunningTotal}
                </span>
              </h5>
              {renderTransactionTable(unassignedWithTotal)}
            </div>
          )}

          {periodsWithTotals.length === 0 && unassignedWithTotal.length === 0 && (
            <div>No transactions yet</div>
          )}
        </>
      )}
    </div>
  );
};
